import {
  glassBoxBase,
  glassPaneCenter,
  glassPaneNormalAxis,
  type CollisionWorld,
  type MapDef,
} from "@rietz/shared";
import type { GlassPanes } from "../render/glass.js";
import type { GameAudio } from "../audio/synth.js";
import type { Fx } from "./fx.js";

/**
 * Which window panes are broken, and everything that follows from one breaking.
 * The SERVER decides — a `glass` event arrives and this applies it; nothing is
 * predicted, because a pane is a wall until the server says it is a hole.
 *
 * Three consequences, in order: the collision solid at `glassBoxBase(map) + id`
 * is switched off (prediction, bullets and the interact raycast all skip it
 * afterwards), the instance stops being drawn, and — for a break that just
 * happened, not the join-time backlog — shards and a crack.
 */

export interface GlassStateOptions {
  /** Getters: the map and the collision world are both swapped wholesale. */
  map: () => MapDef | null;
  collision: () => CollisionWorld;
  /** The current map's panes, or `null` on a map with none. */
  glass: () => GlassPanes | null;
  fx: Fx;
  audio: GameAudio;
}

export class GlassState {
  private readonly o: GlassStateOptions;
  private readonly broken = new Set<number>();

  constructor(opts: GlassStateOptions) {
    this.o = opts;
  }

  /** A new map: every pane is whole again. */
  reset(): void {
    this.broken.clear();
  }

  isBroken(id: number): boolean {
    return this.broken.has(id);
  }

  /**
   * Pane `id` is gone. `loud` fires the effects — `false` for the `glassState`
   * backlog, which is a window broken before we joined.
   */
  apply(id: number, loud: boolean): void {
    const map = this.o.map();
    if (map === null) return;
    const panes = map.glass;
    if (panes === undefined) return;
    const pane = panes[id];
    // A malformed id is a server we do not understand; a repeat is the
    // `glassState` backlog overlapping an event and must not fire twice.
    if (pane === undefined || this.broken.has(id)) return;
    this.broken.add(id);

    this.o.collision().setSolidEnabled(glassBoxBase(map) + id, false);
    this.o.glass()?.break(id);
    if (!loud) return;

    const at = glassPaneCenter(pane);
    this.o.fx.shatter(at, glassPaneNormalAxis(pane));
    this.o.audio.shatter(at);
  }
}
